/**
 * PanelDrawer Component
 * Reusable drawer wrapper with width persistence, panel modes, and keyboard shortcuts
 * Ported from original Visual Designer
 * Following PLUGIN_EXCELLENCE_PLAYBOOK.md - CSS variables, performance, UX
 */

const React = (globalThis as any).React;
const { useState, useEffect, useCallback, useRef } = React;
const ReactDOM = (globalThis as any).ReactDOM;

export type PanelMode = 'normal' | 'expanded' | 'maximized';

export interface PanelDrawerProps {
    title: any; // React.ReactNode
    open: boolean;
    onClose: () => void;
    children: any; // React.ReactNode
    storageKeyPrefix: string;
    defaultWidth?: number;
    expandedWidth?: number;
    overlayTopOffset?: number; // e.g., 120 for header + toolbar
    overlayBottomOffset?: number; // e.g., 48 for footer
    onCollapse?: () => void; // Optional collapse handler - shows collapse button in front of title
}

// Track the foremost open drawer so only it handles global keys
const activeRef = { current: null as null | number };

export function PanelDrawer({
    title,
    open,
    onClose,
    children,
    storageKeyPrefix,
    defaultWidth = 480,
    expandedWidth = 720,
    overlayTopOffset = 120, // Below header (64px) + toolbar (56px)
    overlayBottomOffset = 48, // Above footer
    onCollapse, // Optional collapse handler - shows collapse button in front of title
}: PanelDrawerProps) {
    const instanceIdRef = useRef(Date.now() + Math.random());
    const MODE_KEY = `${storageKeyPrefix}:mode`;
    const WIDTH_KEY = `${storageKeyPrefix}:width`;

    const [mode, setMode] = useState(() => {
        try {
            const stored = localStorage.getItem(MODE_KEY);
            return (stored as PanelMode) || 'normal';
        } catch {
            return 'normal';
        }
    });

    const [width, setWidth] = useState(() => {
        try {
            const v = Number(localStorage.getItem(WIDTH_KEY));
            return Number.isFinite(v) && v > 0 ? v : defaultWidth;
        } catch {
            return defaultWidth;
        }
    });

    // Persist mode and width
    useEffect(() => {
        try {
            localStorage.setItem(MODE_KEY, mode);
            localStorage.setItem(WIDTH_KEY, String(width));
        } catch { }
    }, [MODE_KEY, WIDTH_KEY, mode, width]);

    // Keyboard shortcuts (F11 for maximize, Esc to close when maximized)
    useEffect(() => {
        const onKey = (e: any) => {
            if (!open) return;
            if (activeRef.current !== instanceIdRef.current) return;
            if (e.key === 'F11') {
                e.preventDefault();
                setMode((m: any) => m === 'maximized' ? 'normal' : 'maximized');
            }
            if (e.key === 'Escape') {
                if (mode === 'maximized') {
                    setMode('normal');
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode, open, onClose]);

    // Mark this drawer as active when opened
    useEffect(() => {
        if (open) {
            activeRef.current = instanceIdRef.current;
        }
    }, [open]);

    const markActive = useCallback(() => {
        activeRef.current = instanceIdRef.current;
    }, []);

    const styles = {
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
        },
        modeButtons: {
            display: 'flex',
            gap: '4px',
        },
        modeButton: {
            padding: '4px 8px',
            background: 'var(--card-bg)',
            color: 'var(--main-text)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        modeButtonActive: {
            background: 'var(--main-accent)',
            color: 'var(--button-text)',
            borderColor: 'var(--main-accent)',
        },
        drawer: {
            background: 'var(--modal-bg)',
            color: 'var(--main-text)',
        },
        maximizedOverlay: {
            position: 'fixed' as const,
            top: `${overlayTopOffset}px`,
            left: 0,
            right: 0,
            bottom: `${overlayBottomOffset}px`,
            zIndex: 1001,
            background: 'var(--modal-overlay, rgba(11, 28, 61, 0.98))',
            backdropFilter: 'blur(8px)',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column' as const,
        },
        maximizedHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '1px solid var(--border-color)',
        },
        maximizedContent: {
            flex: 1,
            overflow: 'auto' as const,
        },
    };

    const header = React.createElement('div', { style: styles.header },
        // Left side: optional collapse button + title (symmetric with left panel)
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
            // Collapse button (if onCollapse provided, matches left panel style)
            onCollapse && React.createElement('button', {
                type: 'button',
                onClick: (e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCollapse();
                },
                style: {
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8b949e',
                    fontSize: '14px',
                    borderRadius: '4px',
                    transition: 'all 0.15s ease',
                },
                title: 'Collapse panel',
                'aria-label': 'Collapse panel',
            }, '▶'),
            React.createElement('span', { 
                style: { 
                    fontSize: '12px', 
                    fontWeight: 600, 
                    color: 'var(--pulse-cyan, #00E7F6)', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.5px' 
                } 
            }, title)
        ),
        // Right side: mode buttons + close
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
            // Mode buttons
            React.createElement('div', { style: styles.modeButtons },
                React.createElement('button', {
                    onClick: () => setMode('normal'),
                    style: {
                        ...styles.modeButton,
                        ...(mode === 'normal' ? styles.modeButtonActive : {}),
                    },
                    title: 'Normal mode (F11 to maximize)',
                    'aria-label': 'Normal mode'
                }, 'Normal'),
                React.createElement('button', {
                    onClick: () => setMode('expanded'),
                    style: {
                        ...styles.modeButton,
                        ...(mode === 'expanded' ? styles.modeButtonActive : {}),
                    },
                    title: 'Expanded mode',
                    'aria-label': 'Expanded mode'
                }, 'Expand'),
                React.createElement('button', {
                    onClick: () => setMode('maximized'),
                    style: {
                        ...styles.modeButton,
                        ...(mode === 'maximized' ? styles.modeButtonActive : {}),
                    },
                    title: 'Maximized mode (F11 to toggle)',
                    'aria-label': 'Maximized mode'
                }, 'Max')
            ),
            // Close button - always visible
            React.createElement('button', {
                type: 'button',
                onClick: (e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                },
                style: {
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--main-text-secondary, rgba(255, 255, 255, 0.6))',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.15s ease',
                    lineHeight: 1,
                    marginLeft: '4px',
                },
                onMouseEnter: (e: any) => {
                    e.currentTarget.style.color = 'var(--main-accent, #00e7f6)';
                    e.currentTarget.style.background = 'rgba(0, 231, 246, 0.1)'; // token: --pulse-cyan
                },
                onMouseLeave: (e: any) => {
                    e.currentTarget.style.color = 'var(--main-text-secondary, rgba(255, 255, 255, 0.6))';
                    e.currentTarget.style.background = 'transparent';
                },
                title: 'Close (Esc)',
                'aria-label': 'Close'
            }, '×')
        )
    );

    // Normal / Expanded Drawer (using Ant Design Drawer if available, otherwise custom)
    if (mode !== 'maximized') {
        const drawerWidth = mode === 'expanded' ? Math.max(expandedWidth, width) : width;

        // Use Ant Design Drawer if available
        const { Drawer } = (globalThis as any).antd || {};

        if (Drawer) {
            return React.createElement(Drawer, {
                title: header,
                placement: 'right',
                onClose: onClose,
                open: open,
                width: drawerWidth,
                destroyOnClose: true,
                mask: false, // No dark overlay on canvas
                closable: false, // We have our own close button in header
                onMouseEnter: markActive,
                style: styles.drawer,
                rootStyle: {
                    top: `${overlayTopOffset}px`,
                    bottom: `${overlayBottomOffset}px`,
                    height: `calc(100vh - ${overlayTopOffset}px - ${overlayBottomOffset}px)`,
                },
                styles: {
                    wrapper: {
                        top: `${overlayTopOffset}px`,
                        bottom: `${overlayBottomOffset}px`,
                        height: `calc(100vh - ${overlayTopOffset}px - ${overlayBottomOffset}px)`,
                    },
                    content: {
                        background: 'var(--modal-bg, rgba(11, 28, 61, 0.98))',
                        borderLeft: '1px solid rgba(0, 231, 246, 0.2)', // token: --pulse-cyan
                    },
                    header: {
                        background: 'rgba(5, 15, 35, 0.95)',
                        borderBottom: '1px solid rgba(0, 231, 246, 0.2)', // token: --pulse-cyan
                        padding: '12px 16px',
                    },
                    body: {
                        padding: '0',
                        overflow: 'auto',
                    },
                },
            },
                React.createElement('div', {
                    style: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }
                },
                    React.createElement('div', { style: { flex: 1, overflow: 'auto' } }, children)
                )
            );
        }

        // Fallback: Custom drawer implementation
        if (!open) return null;

        return React.createElement('div', {
            style: {
                position: 'fixed',
                top: `${overlayTopOffset}px`,
                right: 0,
                bottom: `${overlayBottomOffset}px`,
                width: `${drawerWidth}px`,
                background: 'var(--modal-bg, rgba(11, 28, 61, 0.98))',
                borderLeft: '1px solid rgba(0, 231, 246, 0.2)', // token: --pulse-cyan
                boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.4)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideInRight 0.3s ease',
            },
            onMouseEnter: markActive,
        },
            React.createElement('div', {
                style: {
                    padding: '16px',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }
            },
                header // header already contains close button
            ),
            React.createElement('div', {
                style: {
                    flex: 1,
                    overflow: 'auto',
                    padding: '16px',
                }
            }, children)
        );
    }

    // Maximized overlay rendered via portal
    if (!open) return null;

    if (ReactDOM && ReactDOM.createPortal) {
        return ReactDOM.createPortal(
            React.createElement('div', {
                onMouseEnter: markActive,
                style: styles.maximizedOverlay,
            },
                React.createElement('div', { style: styles.maximizedHeader },
                    header // header already contains close button
                ),
                React.createElement('div', { style: styles.maximizedContent }, children)
            ),
            document.body
        );
    }

    // Fallback: render directly if portal not available
    return React.createElement('div', {
        onMouseEnter: markActive,
        style: styles.maximizedOverlay,
    },
        React.createElement('div', { style: styles.maximizedHeader },
            header // header already contains close button
        ),
        React.createElement('div', { style: styles.maximizedContent }, children)
    );
}